import React, { useEffect, useRef, useState } from 'react';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { ChallengeStatus, FlowStatus } from '@/api/generated';
import { LoginCard } from '@/components/login-card';
import { LoginButton } from '@/components/ui/login-button';
import { Page } from '@/components/ui/page';
import { StatusMessage } from '@/components/ui/status-message';
import { useLanguage } from '@/contexts/language-context';
import type { UseMfaOrchestratorReturn } from '@/hooks/use-mfa-orchestrator.types';
import {
  EXTERNAL_SSO_CODE_MESSAGE_TYPE,
  EXTERNAL_SSO_CODE_PARAM,
} from './external-sso-popup';

const POPUP_NAME = 'external-sso';
const POPUP_FEATURES = 'popup,width=520,height=680';

interface ExternalSsoFactorProps {
  submitResponse: UseMfaOrchestratorReturn['submitResponse'];
  redirectUrl: string;
  hasAlternatives?: boolean;
  onShowAlternatives?: () => void;
  username?: string;
  onBack?: () => void;
  isEscalationFlow?: boolean;
}

export const ExternalSsoFactor: React.FC<ExternalSsoFactorProps> = ({
  submitResponse,
  redirectUrl,
  hasAlternatives,
  onShowAlternatives,
  username,
  onBack,
  isEscalationFlow,
}) => {
  const { t } = useLanguage();
  const popupRef = useRef<Window | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== popupRef.current ||
        event.data?.type !== EXTERNAL_SSO_CODE_MESSAGE_TYPE ||
        typeof event.data.code !== 'string'
      ) {
        return;
      }

      popupRef.current = null;
      setIsSubmitting(true);
      void submitResponse({ [EXTERNAL_SSO_CODE_PARAM]: event.data.code }).then(
        result => {
          if (
            result !== FlowStatus.COMPLETED &&
            result !== ChallengeStatus.APPROVED
          ) {
            setIsSubmitting(false);
            setError(t('mfa.genericError'));
          }
        }
      );
    };

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
  }, [submitResponse, t]);

  useEffect(() => () => popupRef.current?.close(), []);

  const openPopup = () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();

      return;
    }

    const popup = window.open(redirectUrl, POPUP_NAME, POPUP_FEATURES);

    if (!popup) {
      setError(t('externalSso.popupBlocked'));

      return;
    }

    popupRef.current = popup;
    setError(null);
  };

  return (
    <Page className='flex flex-col justify-center items-center'>
      <LoginCard
        hasAlternatives={hasAlternatives}
        onShowAlternatives={onShowAlternatives}
        username={username}
        onBack={onBack}
        isEscalationFlow={isEscalationFlow}
      >
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-1'>
            <Text as='h1' size={TextSizes.MD} weight={FontWeights.BOLD}>
              {t('externalSso.title')}
            </Text>
            <Text size={TextSizes.SM} className='text-secondary'>
              {t('externalSso.description')}
            </Text>
          </div>

          <div className='flex flex-col gap-4'>
            {error && <StatusMessage>{error}</StatusMessage>}

            <LoginButton
              data-testid='external-sso-continue-button'
              type='button'
              size={Sizes.LG}
              className='w-full'
              onClick={openPopup}
              isLoading={isSubmitting}
            >
              {t('common.continue')}
            </LoginButton>
          </div>
        </div>
      </LoginCard>
    </Page>
  );
};
